import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useHttpMutation, useHttpQuery } from '@/api';
import { dispatch, getStoreState, Store } from '@/redux';
import { updateUserCoins, updateUserState } from '@/redux/reducers';
import { LoggerService } from '@/shared/logger';
import {
  balanceQueryKeys,
  clockOffsetMs,
  COIN_REWARD_URL,
  COIN_WALLET_URL,
  CoinRewardResponse,
  CoinWallet,
  coinWalletQueryKey,
  readCooldown,
  rewardStatus,
  RewardStatus,
} from './coin-wallet';

/** Re-renders every `intervalMs` while enabled; the interval is always cleared. */
export const useNow = (enabled: boolean, intervalMs = 1000): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);
  return now;
};

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export const usePrefersReducedMotion = (): boolean => {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(REDUCED_MOTION_QUERY).matches
      : false;
  const [reduced, setReduced] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
};

export type ClaimState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'claiming' }
  | {
      readonly kind: 'success';
      readonly claimed: number;
      readonly balance: number;
    }
  | {
      readonly kind: 'error';
      readonly message: string;
      readonly cooldown: boolean;
    };

const GENERIC_CLAIM_ERROR =
  "We couldn't confirm your daily deal. Refresh your wallet before trying again.";

export interface UseCoinWallet {
  readonly enabled: boolean;
  readonly wallet: CoinWallet | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
  /** Server-corrected "now". */
  readonly now: number;
  readonly status: RewardStatus | null;
  readonly claim: ClaimState;
  readonly claimReward: () => void;
  readonly resetClaim: () => void;
}

/**
 * Account-only wallet state. Disabled (no request) for guests and signed-out
 * sessions. Query keys include the user id so a different user never sees a
 * previous user's cached wallet.
 */
export const useCoinWallet = (): UseCoinWallet => {
  const userId = useSelector((s: Store) => s.userReducer.id);
  const isGuest = useSelector((s: Store) => s.userReducer.userType === 'guest');
  const enabled = !!userId && !isGuest;
  const queryKey = coinWalletQueryKey(userId);
  const queryClient = useQueryClient();

  const query = useHttpQuery<CoinWallet>(
    queryKey,
    COIN_WALLET_URL,
    'GET',
    undefined,
    { enabled, staleTime: 15_000, refetchOnMount: 'always', retry: 1 }
  );
  const wallet = enabled ? query.data : undefined;
  useEffect(() => {
    if (
      wallet &&
      query.isFetchedAfterMount &&
      !query.isFetching &&
      !query.isError &&
      getStoreState().userReducer.id === userId &&
      getStoreState().userReducer.coins !== wallet.balance
    ) {
      dispatch(updateUserCoins(wallet.balance));
    }
  }, [
    wallet,
    userId,
    query.isFetchedAfterMount,
    query.isFetching,
    query.isError,
  ]);

  const offset = useMemo(
    () => clockOffsetMs(wallet?.serverTime, query.dataUpdatedAt || Date.now()),
    [wallet?.serverTime, query.dataUpdatedAt]
  );

  const coolingDown = !!wallet?.nextRewardAt;
  const tick = useNow(enabled && coolingDown);
  const now = tick + offset;
  const status = wallet ? rewardStatus(wallet, now) : null;

  // When a cooldown elapses, ask the server instead of trusting the device.
  const prevKind = useRef<RewardStatus['kind'] | null>(null);
  useEffect(() => {
    if (prevKind.current === 'cooldown' && status?.kind === 'ready') {
      queryClient.invalidateQueries(queryKey);
    }
    prevKind.current = status?.kind ?? null;
  }, [status?.kind, queryClient, queryKey]);

  const [claim, setClaim] = useState<ClaimState>({ kind: 'idle' });
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // User switch: drop any claim result that belonged to the previous account.
  useEffect(() => {
    setClaim({ kind: 'idle' });
    inFlight.current = false;
  }, [userId]);

  const { mutateAsync } = useHttpMutation<CoinRewardResponse, void>(
    COIN_REWARD_URL,
    'POST'
  );

  const claimReward = useCallback(() => {
    if (!enabled || inFlight.current) return;
    inFlight.current = true;
    const forUser = userId;
    const forKey = coinWalletQueryKey(forUser);
    setClaim({ kind: 'claiming' });

    mutateAsync()
      .then(data => {
        if (getStoreState().userReducer.id !== forUser) return;
        queryClient.setQueryData(forKey, data);
        dispatch(updateUserCoins(Number(data.balance) || 0));
        dispatch(updateUserState({ lastFreeSpin: data.lastFreeSpin }));
        balanceQueryKeys(forUser)
          .filter(k => k !== forKey)
          .forEach(k => queryClient.invalidateQueries(k));
        if (mounted.current) {
          setClaim({
            kind: 'success',
            claimed: data.claimed,
            balance: data.balance,
          });
        }
      })
      .catch(err => {
        if (getStoreState().userReducer.id !== forUser) return;
        const cooldown = readCooldown(err);
        if (cooldown) {
          queryClient.setQueryData<CoinWallet | undefined>(forKey, old =>
            old
              ? {
                  ...old,
                  nextRewardAt: cooldown.nextRewardAt,
                  serverTime: cooldown.serverTime || new Date().toISOString(),
                  rewardAvailable: false,
                }
              : old
          );
          queryClient.invalidateQueries(forKey);
        } else {
          LoggerService.error('Daily deal claim failed', err);
          queryClient.invalidateQueries(forKey);
        }
        if (mounted.current) {
          setClaim({
            kind: 'error',
            message: cooldown ? cooldown.message : GENERIC_CLAIM_ERROR,
            cooldown: !!cooldown,
          });
        }
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [enabled, userId, mutateAsync, queryClient]);

  const resetClaim = useCallback(() => setClaim({ kind: 'idle' }), []);
  const { refetch: rawRefetch } = query;
  const refetch = useCallback(() => {
    rawRefetch();
  }, [rawRefetch]);

  return {
    enabled,
    wallet,
    isLoading: enabled && query.isLoading,
    isError: enabled && query.isError && !wallet,
    refetch,
    now,
    status,
    claim,
    claimReward,
    resetClaim,
  };
};
